import http from 'src/infrastructure/http'
import { parseFailedResponse, parseGoodResponse } from 'src/infrastructure/parse-axios-response'
import {
  GetTransfersListsPayload,
  GenericApiErrors,
  FiltersSearchLists,
  GetTransfersListsData,
  TransferListPagesData,
  DownloadTransferListPagesInvoicesPayload,
  IssueTransferListInvoicesData,
  IssueTransferPageInvoicesData,
  TransferPageDetails,
  PageCageDetails,
  DownloadInvoicesData,
  ReceiveUser,
  ReceiveStartPayload,
  ReceiveData,
  ValidateProductBarcodePayload,
  ValidateMovementAddressPayload,
  ReceiveItem,
  ReceiveBatchesMovementPayload,
  ReceiveUnfitMovementApiPayload,
  ReceiveMovementData,
  GetReceiveStatusPayload,
  ReceiveDataStatus,
  CageAddressInfo,
  ReceiveSupervisorCountBatchesPayload,
  ReceiveSupervisorProductNotFoundPayload,
  ReceiveSupervisorProductNotFoundData,
  SearchTransferCagesByFiltersPayload,
  TransferCageData,
  TransferCageDetails,
} from './TransfersTypes'
import { camelToSnakeCase } from 'src/utils/formatString'
import { fileDownload } from 'src/infrastructure/fileDownload'
import { formatDateIsoReplaceHour } from 'src/utils/formatDate'

const getSearchFilters = async () => {
  try {
    const result = await http.get('/v2/transfers/filters')

    return parseGoodResponse<FiltersSearchLists>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getTransfersLists = async (
  payload: GetTransfersListsPayload,
) => {
  try {
    const {
      idSourceFc,
      idDestinationFc,
      createdDate,
      shippingDate,
      priorities,
      idsStatus,
      page,
      perPage,
      order,
      orderBy,
    } = payload

    const convertedOrderBy = orderBy ? camelToSnakeCase(orderBy.toString()) : undefined

    const body = {
      id_source_fc: idSourceFc || null,
      id_destination_fc: idDestinationFc || null,
      created_date: createdDate || null,
      shipping_date: shippingDate || null,
      priorities,
      ids_status: idsStatus,
      order,
      order_by: convertedOrderBy,
    }

    const result = await http.post(`/v2/transfers/search?page=${page}&per_page=${perPage}`, body)

    return parseGoodResponse<GetTransfersListsData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const removeTransferList = async (idList: number) => {
  try {
    const result = await http.put(`/v2/transfers/cancel/list/${idList}`)

    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const downloadTransfersListsInvoices = async (idsLists: number[]) => {
  try {
    const result = await http.post(
      '/v2/transfers/lists/download-invoices',
      { ids_lists: idsLists },
      { responseType: 'blob' },
    )

    const filename = result.headers['file-name']
    const errors = JSON.parse(result.headers.errors)

    const downloadData = {
      file_download: () => fileDownload(result.data, filename),
      errors,
    }

    return parseGoodResponse<DownloadInvoicesData>(downloadData)
  } catch (err: any) {
    if (err.response?.data instanceof Blob) {
      try {
        const errorText = await err.response.data.text()
        const errorJson = JSON.parse(errorText)
        return parseFailedResponse<any, any>(GenericApiErrors.Failed, errorJson)
      } catch (parseError: any) {
        return parseFailedResponse<any, any>(GenericApiErrors.Failed, {
          message: 'Erro desconhecido ao processar a resposta',
        })
      }
    }
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const issueTransferListInvoices = async (idList: number) => {
  try {
    const result = await http.post(`/v2/transfers/list/${idList}/nfe-issue`)

    return parseGoodResponse<IssueTransferListInvoicesData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getTransferListPages = async (idList: number) => {
  try {
    const result = await http.get(`/v2/transfers/list/${idList}/pages`)

    return parseGoodResponse<TransferListPagesData[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const removeTransferListPage = async (idPage: number) => {
  try {
    const result = await http.put(`/v2/transfers/cancel/page/${idPage}`)

    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const downloadTransferListPagesInvoices = async (payload: DownloadTransferListPagesInvoicesPayload) => {
  try {
    const { idList, idsPages } = payload

    const body = {
      id_list: idList,
      ids_pages: idsPages,
    }

    const result = await http.post('/v2/transfers/list/pages/download-invoices', body, { responseType: 'blob' })

    const filename = result.headers['file-name']
    const errors = JSON.parse(result.headers.errors)

    const downloadData = {
      file_download: () => fileDownload(result.data, filename),
      errors,
    }

    return parseGoodResponse<DownloadInvoicesData>(downloadData)
  } catch (err: any) {
    if (err.response?.data instanceof Blob) {
      try {
        const errorText = await err.response.data.text()
        const errorJson = JSON.parse(errorText)
        return parseFailedResponse<any, any>(GenericApiErrors.Failed, errorJson)
      } catch (parseError: any) {
        return parseFailedResponse<any, any>(GenericApiErrors.Failed, {
          message: 'Erro desconhecido ao processar a resposta',
        })
      }
    }
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const issueTransferListPageInvoices = async (idPage: number) => {
  try {
    const result = await http.post(`/v2/transfers/page/${idPage}/nfe-issue`)

    return parseGoodResponse<IssueTransferPageInvoicesData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getTransferPageDetails = async (idPage: Number) => {
  try {
    const result = await http.get(`/v2/transfers/page/${idPage}/details`)
    return parseGoodResponse<TransferPageDetails>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const issueTransferListPageCageInvoice = async (idCage: number) => {
  try {
    const result = await http.post(`/v2/transfers/cage/${idCage}/nfe-issue`)

    return parseGoodResponse<PageCageDetails>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const downloadTransferListPageCageInvoice = async (idCage: number) => {
  try {
    const body = {
      id_cage: idCage,
    }

    const result = await http.post('/v2/transfers/cage/download-invoice', body, { responseType: 'blob' })

    const filename = result.headers['file-name']

    return parseGoodResponse<() => void>(() => fileDownload(result.data, filename))
  } catch (err: any) {
    if (err.response?.data instanceof Blob) {
      try {
        const errorText = await err.response.data.text()
        const errorJson = JSON.parse(errorText)
        return parseFailedResponse<any, any>(GenericApiErrors.Failed, errorJson)
      } catch (parseError: any) {
        return parseFailedResponse<any, any>(GenericApiErrors.Failed, {
          message: 'Erro desconhecido ao processar a resposta',
        })
      }
    }
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const receiveValidateBadge = async (badge: string) => {
  try {
    const result = await http.get('/v2/transfers/receive/validate-badge', {
      headers: { 'user-barcode': badge },
    })

    const data = {
      badge,
      name: result.data.data.name,
    }

    return parseGoodResponse<ReceiveUser>(data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const receiveStart = async (payload: ReceiveStartPayload) => {
  try {
    const {
      badge, idFulfillmentCenter, cageAddress, invoiceAccessKey,
    } = payload

    const body = {
      id_fulfillment_center: idFulfillmentCenter,
      cage_address: cageAddress,
      invoice_access_key: invoiceAccessKey,
    }

    const result = await http.post('/v2/transfers/receive/start', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<ReceiveData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getReceiveStatus = async (payload: GetReceiveStatusPayload) => {
  try {
    const result = await http.get(`/v2/transfers/receive/status/${payload.idCage}`, {
      headers: { 'user-barcode': payload.badge },
    })

    return parseGoodResponse<ReceiveDataStatus>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateProductBarcode = async (payload: ValidateProductBarcodePayload) => {
  try {
    const { badge, idModel, barcode } = payload

    const result = await http.post('/v2/transfers/receive/validate-product', {
      id_model: idModel,
      product_barcode: barcode,
    }, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateMovementAddress = async (payload: ValidateMovementAddressPayload) => {
  try {
    const {
      idModel, address, addressToValidate, isGondola,
    } = payload

    const result = await http.post('/v2/warehouse/location/validate-address', {
      id_model: idModel,
      address,
      address_to_validate: addressToValidate,
      is_gondola: isGondola,
    })

    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const receiveValidateSupervisorBadge = async (badge: string) => {
  try {
    const result = await http.get('/v2/transfers/receive/supervisor/validate', {
      headers: { 'user-barcode': badge },
    })

    const data = {
      badge,
      name: result.data.data.name,
    }

    return parseGoodResponse<ReceiveUser>(data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const receiveSupervisorProductNotFound = async (payload: ReceiveSupervisorProductNotFoundPayload) => {
  try {
    const { badge, idCage, item } = payload

    const body = {
      id_cage: idCage,
      id_model: item.idModel,
    }

    const result = await http.post('v2/transfers/receive/supervisor/product-not-found', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<ReceiveSupervisorProductNotFoundData>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const receiveSupervisorCountBatches = async (payload: ReceiveSupervisorCountBatchesPayload) => {
  try {
    const {
      badge, idCage, idModel, batches,
    } = payload

    const body = {
      id_cage: idCage,
      id_model: idModel,
      batches: batches.map((batch) => ({
        quantity: batch.quantity,
        expiration_date: formatDateIsoReplaceHour(batch.expirationDate),
      })),
    }

    const result = await http.post('/v2/transfers/receive/supervisor/count-batches', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<ReceiveData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const receiveMovement = async (payload: ReceiveBatchesMovementPayload) => {
  try {
    const {
      badge, idCage, item, startedAt,
    } = payload

    const body = {
      id_cage: idCage,
      id_location: item.bestPosition.id,
      batches: item.batches.map((batch) => ({
        id_batch_due_date: batch.idBatchStockExpiration,
        quantity: batch.quantity,
      })),
      started_at: startedAt.toISOString(),
    }

    const result = await http.post('v2/transfers/receive/movement', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<ReceiveMovementData>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const receiveUnfitMovement = async (payload: ReceiveUnfitMovementApiPayload) => {
  try {
    const {
      badge, items, item, startedAt,
    } = payload

    const body = {
      movement_groups: items.map((_item) => ({
        id_model: _item.idModel,
        product_description: _item.productDescription,
        batches: _item.batches.map((batch) => ({
          id_batch_receivement: batch.idBatchReceivement,
          id_batch_stock_expiration: batch.idBatchStockExpiration,
          batch_number: batch.batchNumber,
          expiration_date: batch.expirationDate,
          quantity: batch.quantity,
        })),
        order: _item.order,
        volume: _item.volume,
        product_volume: _item.productVolume,
        is_pallet: _item.isPallet,
        is_gondola: _item.isGondola,
        best_position: {
          id: _item.bestPosition.id,
          address: _item.bestPosition.address,
        },
        gondola_address: {
          id: _item.gondolaAddress.id,
          address: _item.gondolaAddress.address,
        },
        done: _item.done || false,
        is_internal: _item.isInternal,
      })),
      unfit_group: {
        id_model: item.idModel,
        product_description: item.productDescription,
        batches: item.batches.map((batch) => ({
          id_batch_receivement: batch.idBatchReceivement,
          id_batch_stock_expiration: batch.idBatchStockExpiration,
          batch_number: batch.batchNumber,
          expiration_date: batch.expirationDate,
          quantity: batch.quantity,
        })),
        order: item.order,
        volume: item.volume,
        product_volume: item.productVolume,
        is_pallet: item.isPallet,
        is_gondola: item.isGondola,
        best_position: {
          id: item.bestPosition.id,
          address: item.bestPosition.address,
        },
        gondola_address: {
          id: item.gondolaAddress.id,
          address: item.gondolaAddress.address,
        },
        done: item.done || false,
        is_internal: item.isInternal,
      },
      quantity: item.batches.reduce((sum, batch) => sum + batch.quantity, 0),
      send_metric: {
        metric_code: 'MOVEMENT_TRANSFER',
        started_at: startedAt.toISOString(),
      },
    }

    const result = await http.post('/v2/warehouse/products/unfit-group', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<ReceiveItem[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getCageAddressInfo = async (address: string) => {
  try {
    const result = await http.get(`v2/transfers/cage/${address}`)

    return parseGoodResponse<CageAddressInfo>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const changeCageFc = async (idLocation: number, idFc: number) => {
  try {
    const result = await http.patch('v2/transfers/cage/change-fc', {
      id_location: idLocation,
      id_fc: idFc,
    })

    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const searchCagesByFilters = async (payload: SearchTransferCagesByFiltersPayload) => {
  try {
    const body = {
      status: payload.status,
      initial_date: payload.initialDate,
      final_date: payload.finalDate,
      id_source_fc: payload.idSourceFc,
      id_destination_fc: payload.idDestinationFc,
      cage_address: payload.cageAddress,
      invoice_access_key: payload.invoiceAccessKey,
    }
    const result = await http.post('v2/transfers/cages/search-by-filters', body)

    return parseGoodResponse<TransferCageData[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getCageDetails = async (idCage: number) => {
  try {
    const result = await http.get(`v2/transfers/cages/${idCage}/details`)

    return parseGoodResponse<TransferCageDetails>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export default {
  getSearchFilters,
  getTransfersLists,
  removeTransferList,
  downloadTransfersListsInvoices,
  issueTransferListInvoices,
  getTransferListPages,
  removeTransferListPage,
  downloadTransferListPagesInvoices,
  issueTransferListPageInvoices,
  getTransferPageDetails,
  issueTransferListPageCageInvoice,
  downloadTransferListPageCageInvoice,
  receiveValidateBadge,
  receiveStart,
  getReceiveStatus,
  validateProductBarcode,
  validateMovementAddress,
  receiveValidateSupervisorBadge,
  receiveSupervisorProductNotFound,
  receiveSupervisorCountBatches,
  receiveMovement,
  receiveUnfitMovement,
  getCageAddressInfo,
  changeCageFc,
  searchCagesByFilters,
  getCageDetails,
}
